import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { DEFAULT_IMPORT_STOPWORDS, normalizeStopwordList } from './importStopwords';

/**
 * Firestore accessors for the admin-editable reference stopword list
 * (`admin_config/import_stopwords`). Same shape and lifecycle as
 * derivationSettings.ts: defaults live in code, the doc is created on the first
 * admin save, and every read falls back to the defaults so an import is never
 * blocked by a missing or unreadable config.
 *
 * The matching rules themselves live in the pure importStopwords.ts.
 */

const ADMIN_CONFIG_COLLECTION = 'admin_config';
const IMPORT_STOPWORDS_DOC_ID = 'import_stopwords';

/** Shape of the `admin_config/import_stopwords` document. */
export interface ImportStopwordSettings {
  /** Words/phrases stripped from Shopify-derived reference strings. */
  stopwords: string[];
  /** Epoch millis of the last save. Display only. */
  updatedAt?: number;
  /** UID of the admin who last saved. Display only. */
  updatedBy?: string;
}

function importStopwordsDocRef() {
  return doc(db, ADMIN_CONFIG_COLLECTION, IMPORT_STOPWORDS_DOC_ID);
}

/**
 * Coerce a raw Firestore payload into a clean list. A malformed/absent
 * `stopwords` field falls back to the seed defaults; an explicitly-saved EMPTY
 * array is honoured as "strip nothing".
 */
function readStopwords(data: Partial<ImportStopwordSettings> | undefined): string[] {
  if (!data || !Array.isArray(data.stopwords)) return DEFAULT_IMPORT_STOPWORDS;
  return normalizeStopwordList(data.stopwords);
}

export async function getImportStopwords(): Promise<string[]> {
  try {
    const snapshot = await getDoc(importStopwordsDocRef());
    if (!snapshot.exists()) return DEFAULT_IMPORT_STOPWORDS;
    return readStopwords(snapshot.data() as Partial<ImportStopwordSettings>);
  } catch (err) {
    console.warn('Failed to read import stopwords; using defaults:', err);
    return DEFAULT_IMPORT_STOPWORDS;
  }
}

/**
 * Live stopword list. Emits the seed defaults immediately when the doc doesn't
 * exist yet, and on any read error.
 */
export function subscribeToImportStopwords(
  callback: (stopwords: string[]) => void,
): () => void {
  return onSnapshot(
    importStopwordsDocRef(),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(DEFAULT_IMPORT_STOPWORDS);
        return;
      }
      callback(readStopwords(snapshot.data() as Partial<ImportStopwordSettings>));
    },
    (err) => {
      console.warn('Failed to subscribe to import stopwords; using defaults:', err);
      callback(DEFAULT_IMPORT_STOPWORDS);
    },
  );
}

/** Persist the list. Returns the normalized values actually written. */
export async function saveImportStopwords(
  stopwords: string[],
  uid: string | undefined,
): Promise<string[]> {
  const normalized = normalizeStopwordList(stopwords);
  await setDoc(
    importStopwordsDocRef(),
    {
      stopwords: normalized,
      updatedAt: Date.now(),
      ...(uid ? { updatedBy: uid } : {}),
    },
    { merge: true },
  );
  return normalized;
}

/**
 * Whether the config doc has been written yet — drives the editor's "using
 * built-in defaults, not yet saved" hint.
 */
export async function importStopwordsDocExists(): Promise<boolean> {
  try {
    const snapshot = await getDoc(importStopwordsDocRef());
    return snapshot.exists();
  } catch {
    return false;
  }
}
